import React from 'react';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet-async';
import { Box, Container, Typography, Paper } from '@mui/material';
import { motion } from 'framer-motion';
import { Shield } from 'lucide-react';

const PrivacyPolicy = () => {
  const { t } = useTranslation('privacyPolicy');

  // Get number of sections
  const sectionCount = 7; // Based on translation file

  return (
    <Box sx={{ pt: 12, pb: 8, minHeight: '100vh', backgroundColor: '#f8f9fa' }}>
      <Helmet>
        <title>{t('meta.title')}</title>
        <meta name="description" content={t('meta.description')} />
      </Helmet>

      <Container maxWidth="lg">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          {/* Header */}
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <Box
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 80,
                height: 80,
                borderRadius: '50%',
                background: 'linear-gradient(135deg, #D4A747 0%, #F4E4BC 100%)',
                mb: 3,
                boxShadow: '0 10px 30px rgba(212, 167, 71, 0.2)',
              }}
            >
              <Shield size={40} style={{ color: '#1A1A1A' }} />
            </Box>
            <Typography
              variant="h3"
              sx={{
                fontWeight: 'bold',
                color: '#1A1A1A',
                mb: 2,
                fontSize: { xs: '2rem', md: '3rem' },
              }}
            >
              {t('title')}
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: '#666',
                fontSize: '1.1rem',
              }}
            >
              Last updated: {t('lastUpdated')}
            </Typography>
          </Box>

          {/* Content */}
          <Paper
            elevation={0}
            sx={{
              p: { xs: 3, md: 6 },
              borderRadius: 3,
              background: 'white',
              boxShadow: '0 10px 40px rgba(0,0,0,0.08)',
            }}
          >
            {[...Array(sectionCount)].map((_, index) => {
              const hasListKey = t(`sections.${index}.list`, { returnObjects: true, defaultValue: null });
              const hasList = hasListKey && Array.isArray(hasListKey);
              const hasContactKey = t(`sections.${index}.contact`, { returnObjects: true, defaultValue: null });
              const hasContact = hasContactKey && typeof hasContactKey === 'object';

              return (
                <Box key={index} sx={{ mb: index < sectionCount - 1 ? 4 : 0 }}>
                  <Typography variant="h5" sx={{ fontWeight: 600, mb: 3, color: '#D4A747' }}>
                    {t(`sections.${index}.heading`)}
                  </Typography>
                  <Typography paragraph sx={{ mb: hasList || hasContact ? 3 : 4, lineHeight: 1.8, color: '#444' }}>
                    {t(`sections.${index}.content`)}
                  </Typography>
                  {hasList && (
                    <Box component="ul" sx={{ mb: 4, pl: 3, color: '#444' }}>
                      {hasListKey.map((item, idx) => (
                        <li key={idx}>{item}</li>
                      ))}
                    </Box>
                  )}
                  {hasContact && (
                    <Box sx={{ pl: 3, color: '#444' }}>
                      <Typography>Email: {hasContactKey.email}</Typography>
                      <Typography>Phone: {hasContactKey.phone}</Typography>
                      <Typography>Address: {hasContactKey.address}</Typography>
                    </Box>
                  )}
                </Box>
              );
            })}
          </Paper>
        </motion.div>
      </Container>
    </Box>
  );
};

export default PrivacyPolicy;
